import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Building2, Lock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const schema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});

const HERO_IMG =
  "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZWFsJTIwZXN0YXRlJTIwYnVpbGRpbmclMjBleHRlcmlvcnxlbnwwfHx8fDE3ODM1MTMyMTJ8MA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { currentUser, login } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState("");
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  if (currentUser) return <Navigate to="/" replace />;

  const onSubmit = async (values) => {
    setServerError("");
    try {
      await login(values.username, values.password);
      navigate("/", { replace: true });
    } catch (e) {
      setServerError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen bg-[#fdfbf7]">
      <div className="hidden lg:block relative">
        <img src={HERO_IMG} alt="Modern real estate building" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-black/10" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="text-sm uppercase tracking-widest text-emerald-200 mb-2">Internal Portal</p>
          <h2 className="font-heading text-3xl font-semibold leading-snug">
            Properties, leads &amp; deals — managed in one place.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-11 w-11 rounded-lg bg-emerald-900 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-emerald-950 tracking-tight">
                Aditya Property Consultants
              </h1>
              <p className="text-sm text-slate-500">Staff sign in</p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
                <Input
                  id="username"
                  data-testid="login-username-input"
                  placeholder="Enter your username"
                  autoComplete="username"
                  className="pl-9 h-11 rounded-lg bg-white"
                  {...register("username")}
                />
              </div>
              {errors.username && (
                <p className="text-sm text-red-600" data-testid="login-username-error">{errors.username.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
                <Input
                  id="password"
                  type="password"
                  data-testid="login-password-input"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="pl-9 h-11 rounded-lg bg-white"
                  {...register("password")}
                />
              </div>
              {errors.password && (
                <p className="text-sm text-red-600" data-testid="login-password-error">{errors.password.message}</p>
              )}
            </div>

            {serverError && (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
                data-testid="login-error-message"
              >
                {serverError}
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white font-medium transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md active:translate-y-0"
            >
              {isSubmitting ? "Signing in…" : "Log In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
